import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Printer,
  Type,
  Eye,
  Plus,
  Award,
  ZoomInIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Footer } from "../components/Footer";
import { CompactHeader } from "../components/CompactHeader";
import * as Constants from "../../constants";
import BackLoader from "../../components/backloader";

export default function JudgesMagistrates() {
  const [searchParams] = useSearchParams();
  const [textSize, setTextSize] = useState("normal");
  const searchQuery = searchParams.get("q") || "";
  const listType = searchParams.get("type") || "search";
  const filterType = searchParams.get("filter") || "";
  const selectedDate = searchParams.get("date") || "";

  const [leadership, setLeadership] = useState([]);
  const [category, setCategory] = useState("Judges");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    onLoadTeam();
  }, [category]);

  // Mock data - replace with actual API call
  const onLoadTeam = async () => {
    try {
      setLoading(true);
      const resp = await fetch(`${Constants.apiUrl}/cms/team/${category}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await resp.json();
      setLoading(false);
      if (data.code == 0) {
        setLeadership(data.items);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const mockResults = [
    {
      id: "1",
      SNo: "1",
      Image: `${Constants.baseurl}/assets/landing_images/alogba.png`,
      Name: "	The Hon. Justice K.O. Alogba",
      Designation: "Chief Judge",
      Location: "Ikeja",
      NumberofCourts: "5",
    },
    {
      id: "2",
      SNo: "2",
      Image: `${Constants.baseurl}/assets/landing_images/lpaye.png`,
      Name: "	The Hon. Justice  O.A. Ipaye (Mrs.)",
      Designation: "Hon. Admin Judge",
      Location: "Osborne",
      NumberofCourts: "14",
    },
    {
      id: "3",
      SNo: "3",
      Image: `${Constants.baseurl}/assets/landing_images/mnicol.png`,
      Name: "	The Hon. Justice A.M. Nicol-Clay (Mrs.)",
      Designation: "Hon. Admin Judge",
      Location: "Ikeja",
      NumberofCourts: "18",
    },
    {
      id: "4",
      SNo: "4",
      Image: `${Constants.baseurl}/assets/landing_images/ajcoker.png`,
      Name: "	The Hon. Justice  A.J. Coker (Mrs.)",
      Designation: "Judge",
      Location: "",
      NumberofCourts: "21",
    },
  ];

  const adjustTextSize = (size) => {
    setTextSize(size);
  };

  const getFontSizeClass = () => {
    if (textSize === "large") return "text-lg";
    if (textSize === "small") return "text-sm";
    return "text-base";
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("hearing"))
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    if (statusLower.includes("judgment"))
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    if (statusLower.includes("mention"))
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    if (statusLower.includes("adjournment"))
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Implement CSV/PDF download functionality
    alert("Download functionality would be implemented here");
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <CompactHeader />
      {/* Header Section with Gradient */}
      <section className="relative py-24 bg-hero-light-image dark:bg-hero-dark">
        <div className="absolute inset-0 bg-black/5 dark:bg-black/30"></div>
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              Judges & Magistrates
            </h1>
          </div>
        </div>
      </section>

      <section
        id="strategic-plan-achievements"
        className="py-12 bg-gray-50 dark:bg-gray-800"
      >
        <div className="mx-auto px-4 sm:px-6">
          {/* Accordion Component */}
          <Accordion
            type="single"
            collapsible
            className="w-full container mx-auto shadow-xl rounded-lg overflow-hidden"
            onValueChange={(e) => setCategory(e)}
          >
            {/* 1. Strategic Plan Accordion Item */}
            <AccordionItem
              value="Judges"
              className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700"
            >
              <AccordionTrigger className="p-6 text-xl font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center">
                  <Plus className="w-6 h-6 text-blue-600 mr-3" />
                  Judges
                </div>
              </AccordionTrigger>
              <AccordionContent className="p-6 bg-gray-50 dark:bg-gray-800">
                <section className="bg-white dark:bg-slate-900">
                  <div className="max-w-7xl mx-auto">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <Table className="table-fixed w-full">
                          <TableHeader>
                            <TableRow className="bg-[#155dfc] hover:bg-[#155dfc]">
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                S.No.
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Image
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Name & Designation
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Location
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Court Number
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Actions
                              </TableHead>
                              {/*<TableHead className={`table-head ${getFontSizeClass()}`}>STATUS</TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>ACTIONS</TableHead> */}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {leadership.map((leader, index) => (
                              <TableRow
                                key={leader.id}
                                className={`${
                                  index % 2 === 0
                                    ? "bg-gray-50 dark:bg-slate-700"
                                    : "bg-white dark:bg-slate-800"
                                } hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors`}
                              >
                                <TableCell
                                  className={`${getFontSizeClass()} font-medium text-[#155dfc]`}
                                >
                                  {index + 1}
                                </TableCell>

                                <TableCell>
                                  <img
                                    src={`${Constants.docUrl}/cms/team/${leader.coverImage}`}
                                    alt={leader.fullName}
                                    className="w-16 h-16 rounded-full object-cover border"
                                  />
                                </TableCell>

                                <TableCell>
                                  <div>
                                    <b>{leader.fullName}</b>
                                  </div>
                                  <Badge variant="outline">
                                    {leader.position}
                                  </Badge>
                                </TableCell>

                                <TableCell>{leader.teamLocation}</TableCell>
                                <TableCell>{leader.noOfCourt}</TableCell>

                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#155dfc] hover:text-[#6B1515] hover:bg-[#155dfc]/10"
                                  >
                                    <Eye className="w-4 h-4" />
                                    <ZoomInIcon className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* No Results Message - show if no results */}
                    {mockResults.length === 0 && (
                      <div className="text-center py-16">
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-12 max-w-2xl mx-auto border-2 border-gray-200 dark:border-slate-700">
                          <h3
                            className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-4`}
                          >
                            No Results Found
                          </h3>
                          <p
                            className={`${getFontSizeClass()} text-gray-600 dark:text-gray-400 mb-6`}
                          >
                            We couldn't find any cases matching your search
                            query "{searchQuery}".
                          </p>
                          <Link to="/cause-list">
                            <Button className="bg-[#155dfc] hover:bg-[#6B1515]">
                              Return to Cause List
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </AccordionContent>
            </AccordionItem>

            {/* 2. Recent Achievements Accordion Item */}
            <AccordionItem
              value="Magistrates"
              className="bg-white dark:bg-gray-900"
            >
              <AccordionTrigger className="p-6 text-xl font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center">
                  <Plus className="w-6 h-6 text-blue-600 mr-3" />
                  Magistrates
                </div>
              </AccordionTrigger>
              <AccordionContent className="p-6 bg-gray-50 dark:bg-gray-800">
                <section className="bg-white dark:bg-slate-900">
                  <div className="max-w-7xl mx-auto">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <Table className="table-fixed w-full">
                          <TableHeader>
                            <TableRow className="bg-[#155dfc] hover:bg-[#155dfc]">
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                S.No.
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Image
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Name & Designation
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Location
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Number of Courts
                              </TableHead>
                              <TableHead
                                className={`table-head ${getFontSizeClass()}`}
                              >
                                Actions
                              </TableHead>
                              {/*<TableHead className={`table-head ${getFontSizeClass()}`}>STATUS</TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>ACTIONS</TableHead> */}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {leadership.map((leader, index) => (
                              <TableRow
                                key={leader.id}
                                className={`${
                                  index % 2 === 0
                                    ? "bg-gray-50 dark:bg-slate-700"
                                    : "bg-white dark:bg-slate-800"
                                } hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors`}
                              >
                                <TableCell
                                  className={`${getFontSizeClass()} font-medium text-[#155dfc]`}
                                >
                                  {index + 1}
                                </TableCell>

                                <TableCell>
                                  <img
                                    src={`${Constants.docUrl}/cms/team/${leader.coverImage}`}
                                    alt={leader.fullName}
                                    className="w-16 h-16 rounded-full object-cover border"
                                  />
                                </TableCell>

                                <TableCell>
                                  <div>
                                    <b>{leader.fullName}</b>
                                  </div>
                                  <Badge variant="outline">
                                    {leader.position}
                                  </Badge>
                                </TableCell>

                                <TableCell>{leader.teamLocation}</TableCell>
                                <TableCell>{leader.noOfCourt}</TableCell>

                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#155dfc] hover:text-[#6B1515] hover:bg-[#155dfc]/10"
                                  >
                                    <Eye className="w-4 h-4" />
                                    <ZoomInIcon className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </section>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          {/* End of Accordion Component */}
        </div>
      </section>
      <Footer />
      <BackLoader open={loading} />
    </div>
  );
}
